import React, { ComponentProps, forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildBlocks,
  formatFromMs,
  getBlockIndexFromPos,
  joinMs,
  PAD,
  parseBlock,
  parseTemplateOrMs,
  putBlock,
  safetyParseValue,
} from './helpers';
import { getNumberInRange } from '@/modules/common/helpers/number';

import { TextField } from '@/mui/components';

type Props = Omit<ComponentProps<typeof TextField>, 'onChange'> & {
  value: number | string;
  wrap?: boolean;
  hh?: boolean;
  ms?: boolean;
  min?: number;
  max?: number;
  onChange?: (ms: number) => void;
};

const isMac = navigator.platform.toUpperCase().includes('MAC');

const DurationField = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, wrap = false, hh = true, ms = true, min, max, disabled, ...props }, ref) => {
    const [BLOCKS_CUR, SEP_POS, TEMPLATE, durationTemplate] = useMemo(() => {
      const blocks = buildBlocks({ hh, ms });

      return [
        blocks,
        blocks.map(({ end }) => end),
        blocks.map(({ len }) => '0'.repeat(len)).join(':'),
        blocks
          .map((b) => {
            if (b.key === 'H') {
              return 'HH';
            }

            if (b.key === 'm') {
              return 'mm';
            }

            if (b.key === 's') {
              return 'ss';
            }

            return 'MSS';
          })
          .join(':'),
      ];
    }, [hh, ms]);

    const innerRef = useRef<HTMLInputElement>(null);
    const selectingRef = useRef(false);
    const typedCountRef = useRef(0);
    const lastBlockRef = useRef(0);
    const reselectId = useRef<number | null>(null);
    const minValue = Math.min(min ?? 0, max ?? Number.MAX_SAFE_INTEGER);
    const maxValue = Math.max(min ?? 0, max ?? Number.MAX_SAFE_INTEGER);
    const clampDuration = (duration: number) => getNumberInRange(duration, minValue, maxValue);

    const [text, setText] = useState<string>(() => formatFromMs(clampDuration(safetyParseValue(value)), BLOCKS_CUR));
    const [activeBlock, setActiveBlock] = useState<number>(0);
    const [isComposing, setIsComposing] = useState(false);

    const selectBlock = (idx: number) => {
      const input = innerRef.current;
      const block = BLOCKS_CUR[idx];

      if (input && block) {
        if (reselectId.current != null) {
          window.cancelAnimationFrame(reselectId.current);
          reselectId.current = null;
        }

        selectingRef.current = true;
        window.queueMicrotask(() => {
          window.requestAnimationFrame(() => {
            reselectId.current = window.requestAnimationFrame(() => {
              input.setSelectionRange(block.start, block.end);
              setTimeout(() => {
                selectingRef.current = false;
              }, 0);
            });
          });
        });
      }
    };

    const resetTypedCount = (idx = activeBlock) => {
      typedCountRef.current = 0;
      lastBlockRef.current = idx;
    };

    const commitFromText = (t: string) => {
      let h = 0;
      let mm = 0;
      let ss = 0;
      let mss = 0;

      // eslint-disable-next-line no-restricted-syntax
      for (const b of BLOCKS_CUR) {
        const val = parseBlock(t, b.start, b.end);

        if (b.key === 'H') {
          h = val;
        } else if (b.key === 'm') {
          mm = val;
        } else if (b.key === 's') {
          ss = val;
        } else if (b.key === 'M') {
          mss = val;
        }
      }
      const duration = clampDuration(joinMs(h, mm, ss, mss));
      const normalizedText = formatFromMs(duration, BLOCKS_CUR);

      if (normalizedText !== t) {
        setText(normalizedText);
      }

      if (onChange) {
        onChange(duration);
      }
    };

    useEffect(() => {
      setText(formatFromMs(clampDuration(safetyParseValue(value)), BLOCKS_CUR));
    }, [value, hh, ms, minValue, maxValue]);

    useEffect(() => {
      if (props.autoFocus) {
        innerRef.current?.focus();
        selectBlock(activeBlock);
      }
    }, [props.autoFocus]);

    const setBlockNumLocal = (idx: number, num: number) => {
      const block = BLOCKS_CUR[idx];
      const next = getNumberInRange(num, block.min, block.max);
      const digits = PAD(next, block.len);
      const nextText = putBlock(text, block.start, block.end, digits);

      setText(nextText);

      return nextText;
    };

    const increment = (idx: number, delta: number) => {
      if (wrap) {
        const block = BLOCKS_CUR[idx];
        const curr = parseBlock(text, block.start, block.end);
        let next = curr + delta;
        const range = block.max - block.min + 1;
        next = ((((next - block.min) % range) + range) % range) + block.min;

        const nt = setBlockNumLocal(idx, next);

        commitFromText(nt);
        return;
      }

      const values = BLOCKS_CUR.map((block) => parseBlock(text, block.start, block.end));
      const originalValues = [...values];
      const direction = delta >= 0 ? 1 : -1;
      const iterations = Math.max(1, Math.abs(delta));

      for (let step = 0; step < iterations; step += 1) {
        if (direction > 0) {
          let carryIdx = idx;
          let changed = false;

          while (carryIdx >= 0) {
            const currentBlock = BLOCKS_CUR[carryIdx];
            if (values[carryIdx] < currentBlock.max) {
              values[carryIdx] += 1;
              for (let resetIdx = carryIdx + 1; resetIdx <= idx; resetIdx += 1) {
                values[resetIdx] = BLOCKS_CUR[resetIdx].min;
              }
              changed = true;
              break;
            }
            carryIdx -= 1;
          }

          if (!changed) {
            values.splice(0, values.length, ...originalValues);
            break;
          }
        } else {
          let borrowIdx = idx;
          let changed = false;

          while (borrowIdx >= 0) {
            const currentBlock = BLOCKS_CUR[borrowIdx];
            if (values[borrowIdx] > currentBlock.min) {
              values[borrowIdx] -= 1;
              for (let resetIdx = borrowIdx + 1; resetIdx <= idx; resetIdx += 1) {
                values[resetIdx] = BLOCKS_CUR[resetIdx].max;
              }
              changed = true;
              break;
            }
            borrowIdx -= 1;
          }

          if (!changed) {
            values.splice(0, values.length, ...originalValues);
            break;
          }
        }
      }

      let nt = text;
      BLOCKS_CUR.forEach((block, blockIndex) => {
        nt = putBlock(nt, block.start, block.end, PAD(values[blockIndex], block.len));
      });
      setText(nt);

      commitFromText(nt);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (disabled || isComposing) return;
      const idx = activeBlock;
      const isCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (e.key === 'Enter' || isCtrl) {
        return;
      }

      if (e.key === 'Tab') {
        const atFirst = idx === 0;
        const atLast = idx === BLOCKS_CUR.length - 1;
        if (e.shiftKey) {
          if (!atFirst) {
            e.preventDefault();
            const n = idx - 1;
            setActiveBlock(n);
            resetTypedCount(n);
            selectBlock(n);
          }
        } else if (!atLast) {
          e.preventDefault();
          const n = idx + 1;
          setActiveBlock(n);
          resetTypedCount(n);
          selectBlock(n);
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const n = Math.max(0, idx - 1);
        setActiveBlock(n);
        resetTypedCount(n);
        selectBlock(n);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const n = Math.min(BLOCKS_CUR.length - 1, idx + 1);
        setActiveBlock(n);
        resetTypedCount(n);
        selectBlock(n);
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        setActiveBlock(0);
        resetTypedCount(0);
        selectBlock(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        const last = BLOCKS_CUR.length - 1;
        setActiveBlock(last);
        resetTypedCount(last);
        selectBlock(last);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        resetTypedCount(idx);
        increment(idx, +1);
        selectBlock(idx);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        resetTypedCount(idx);
        increment(idx, -1);
        selectBlock(idx);
        return;
      }

      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();

        if (idx !== lastBlockRef.current) {
          resetTypedCount(idx);
        }

        const block = BLOCKS_CUR[idx];
        const baseStr = typedCountRef.current === 0 ? '0'.repeat(block.len) : text.slice(block.start, block.end);
        const shifted = (baseStr + e.key).slice(-block.len);
        const num = parseInt(shifted, 10) || 0;

        const nt = setBlockNumLocal(idx, num);

        typedCountRef.current += 1;
        const filled = typedCountRef.current >= block.len;
        const atLast = idx === BLOCKS_CUR.length - 1;

        if (filled) {
          commitFromText(nt);

          if (!atLast) {
            const nextIdx = idx + 1;
            setActiveBlock(nextIdx);
            resetTypedCount(nextIdx);
            selectBlock(nextIdx);
          } else {
            selectBlock(idx);
            resetTypedCount(idx);
          }
        } else {
          selectBlock(idx);
        }

        return;
      }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        resetTypedCount(idx);
        const nt = setBlockNumLocal(idx, 0);
        commitFromText(nt);
        selectBlock(idx);
        return;
      }

      if (e.key.length === 1) {
        e.preventDefault();
      }
    };

    const handleSelect: React.ReactEventHandler<HTMLInputElement> = () => {
      const input = innerRef.current;
      if (!input || selectingRef.current) return;

      const pos = input.selectionStart ?? 0;
      const idx = getBlockIndexFromPos(pos, SEP_POS);

      if (idx !== activeBlock) {
        setActiveBlock(idx);
        resetTypedCount(idx);
        selectBlock(idx);
      }
    };

    const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
      e.preventDefault();
      const data = e.clipboardData.getData('text').trim();
      const parsed = parseTemplateOrMs(data, BLOCKS_CUR);

      if (parsed !== null) {
        const next = formatFromMs(parsed, BLOCKS_CUR);

        setText(next);
        commitFromText(next);
        selectBlock(activeBlock);
      }
    };

    return (
      <TextField
        {...props}
        dir="ltr"
        value={text}
        aria-label={`Duration (${durationTemplate})`}
        disabled={disabled}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        onKeyUp={(e) => {
          if (disabled || isComposing) return;
          if (
            /^[0-9]$/.test(e.key) ||
            e.key.startsWith('Arrow') ||
            e.key === 'Home' ||
            e.key === 'End' ||
            e.key === 'Tab'
          ) {
            selectBlock(activeBlock);
          }
        }}
        onMouseDown={() => {
          window.requestAnimationFrame(() => {
            if (innerRef.current) {
              const pos = innerRef.current.selectionStart ?? 0;
              const idx = getBlockIndexFromPos(pos, SEP_POS);

              setActiveBlock(idx);
              resetTypedCount(idx);
              selectBlock(idx);
            }
          });
        }}
        onFocus={(event) => {
          selectBlock(activeBlock);
          props.onFocus?.(event);
        }}
        onSelect={handleSelect}
        onPaste={handlePaste}
        onBlur={(event) => {
          commitFromText(text);
          props.onBlur?.(event);
        }}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        inputRef={(node) => {
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref && 'current' in ref) {
            ref.current = node;
          }

          innerRef.current = node;
        }}
        inputProps={{
          ...(props.inputProps || {}),
          readOnly: true,
          inputMode: 'numeric',
          maxLength: TEMPLATE.length,
        }}
        placeholder={TEMPLATE}
        autoComplete="off"
        spellCheck={false}
      />
    );
  },
);

export default DurationField;
